import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cube, CheckCircle, WarningCircle, XCircle, Trash, ArrowsClockwise, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const HEALTH = {
  healthy: { key: "connected", cls: "border-emerald-500/50 text-emerald-500 bg-emerald-500/5", Icon: CheckCircle },
  expired: { key: "expired", cls: "border-amber-500/50 text-amber-500 bg-amber-500/5", Icon: WarningCircle },
  unreachable: { key: "unreachable", cls: "border-destructive/50 text-destructive bg-destructive/5", Icon: XCircle },
  error: { key: "invalid", cls: "border-destructive/50 text-destructive bg-destructive/5", Icon: XCircle },
};

export const MakerWorldSessionCard = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [handle, setHandle] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/product-hunter/mw-web/status");
      setEnabled(!!data.enabled);
      setSessions(data.sessions || []);
    } catch { /* honest: leave empty */ }
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setJsonText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const openImport = () => { setError(""); setJsonText(""); setHandle(""); setOpen(true); };

  const doImport = async () => {
    setError("");
    let parsed;
    try { parsed = JSON.parse(jsonText); }
    catch { setError(t("ph.mwWeb.invalidJson")); return; }
    setBusy(true);
    try {
      await api.post("/product-hunter/mw-web/session", { handle, storage_state: parsed });
      toast.success(t("ph.mwWeb.imported"));
      setOpen(false); setJsonText(""); setHandle("");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || t("ph.mwWeb.importFailed"));
    } finally { setBusy(false); }
  };

  const doVerify = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/product-hunter/mw-web/session/${id}/verify`);
      // Honest success ONLY on a confirmed authenticated session (positive signal from backend).
      const ok = data.status === "verified" && data.logged_in === true;
      if (ok) {
        const who = data.account?.nickname || data.account?.user_name;
        toast.success(who ? t("ph.mwWeb.verifyOkAs", { who }) : t("ph.mwWeb.verifyOk"));
      } else toast.error(t("ph.mwWeb.verifyBad", { reason: data.reason || data.status }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ph.mwWeb.verifyError"));
    } finally { setBusy(false); }
  };

  const doDelete = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/product-hunter/mw-web/session/${id}`);
      toast.success(t("ph.mwWeb.removed"));
      load();
    } catch { toast.error(t("ph.mwWeb.removeError")); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6" data-testid="mw-web-card">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Cube size={18} className="text-primary" weight="fill" />
          <h3 className="font-heading text-base font-bold">{t("ph.mwWeb.title")}</h3>
          <Badge variant="outline" className="rounded-sm text-[10px]" data-testid="mw-web-enabled-badge">
            {enabled ? t("ph.mwWeb.enabled") : t("ph.mwWeb.disabled")}
          </Badge>
        </div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={openImport}
          data-testid="mw-web-import-button">
          <UploadSimple size={15} className="mr-1.5" />{t("ph.mwWeb.import")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">{t("ph.mwWeb.helper")}</p>

      {sessions.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-4 text-sm text-muted-foreground"
          data-testid="mw-web-empty">
          {t("ph.mwWeb.empty")}
        </div>
      ) : (
        <div className="space-y-2" data-testid="mw-web-session-list">
          {sessions.map((s) => {
            const meta = HEALTH[s.session_health] || { key: "notVerified", cls: "border-border text-muted-foreground bg-secondary/30", Icon: WarningCircle };
            const HIcon = meta.Icon;
            return (
              <div key={s.id} className="flex items-center justify-between gap-3 border border-border rounded-sm px-4 py-3 flex-wrap"
                data-testid={`mw-web-session-${s.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Cube size={16} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.handle}</div>
                    <div className="text-xs text-muted-foreground">
                      {t("ph.mwWeb.cookies", { count: s.session_meta?.cookie_count ?? 0 })}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1.5 border rounded-sm px-2 py-1 ${meta.cls}`}
                    data-testid={`mw-web-health-${s.id}`} data-health={s.session_health || "none"}>
                    <HIcon size={13} /><span className="text-xs">{t(`ph.mwWeb.health.${meta.key}`)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="rounded-sm" disabled={busy}
                    onClick={() => doVerify(s.id)} data-testid={`mw-web-verify-${s.id}`}>
                    <ArrowsClockwise size={14} className="mr-1" />{t("ph.mwWeb.test")}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-sm text-destructive hover:text-destructive"
                    disabled={busy} onClick={() => doDelete(s.id)} data-testid={`mw-web-remove-${s.id}`}>
                    <Trash size={14} className="mr-1" />{t("ph.mwWeb.remove")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="mw-web-import-dialog">
          <DialogHeader>
            <DialogTitle>{t("ph.mwWeb.importTitle")}</DialogTitle>
            <DialogDescription>{t("ph.mwWeb.importDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("ph.mwWeb.handlePlaceholder")} value={handle}
              onChange={(e) => setHandle(e.target.value)} className="rounded-sm"
              data-testid="mw-web-handle-input" />
            <input type="file" accept="application/json,.json" onChange={onFile}
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-sm file:border file:border-border file:bg-secondary file:px-3 file:py-1.5 file:text-xs"
              data-testid="mw-web-file-input" />
            <Textarea rows={6} placeholder={t("ph.mwWeb.jsonPlaceholder")} value={jsonText}
              onChange={(e) => setJsonText(e.target.value)} className="rounded-sm font-mono text-xs"
              data-testid="mw-web-json-input" />
            {error && (
              <div className="text-sm text-destructive flex items-start gap-2" data-testid="mw-web-error">
                <XCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">{t("ph.mwWeb.safetyNote")}</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-sm" onClick={() => setOpen(false)}
              data-testid="mw-web-cancel">{t("common.cancel", "Cancel")}</Button>
            <Button className="rounded-sm" disabled={busy || !jsonText.trim()} onClick={doImport}
              data-testid="mw-web-submit">{busy ? t("ph.mwWeb.importing") : t("ph.mwWeb.import")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MakerWorldSessionCard;
